import * as Sentry from "@sentry/capacitor";
import type { HostLocale } from "@vidimo/shared";
import {
  type ReactNode,
  useEffect,
  useState,
  useSyncExternalStore,
} from "react";
import { IntlProvider } from "use-intl";
import { createIntlPolicy, intlPolicyMode } from "./intl-policy";
import {
  getInterfaceLocaleState,
  type InterfaceLocaleState,
  subscribeInterfaceLocale,
} from "./locale-store";

function useInterfaceLocaleState(): InterfaceLocaleState {
  return useSyncExternalStore(
    subscribeInterfaceLocale,
    getInterfaceLocaleState,
    getInterfaceLocaleState
  );
}

/**
 * The one `IntlProvider` the host app renders in, outermost in the tree so the
 * router, the error boundary, the toaster and both sides of the sign-in swap
 * share it. Its locale comes from the interface-locale store, which the boot
 * seeds before the root exists and the signed-in shell feeds from below, so
 * remounting the shell on sign-in or sign-out never resets the language.
 *
 * No global `timeZone`, `now` or `formats`: every date names its own zone,
 * the property's for property-local times.
 */
export function InterfaceLocaleRoot({ children }: { children: ReactNode }) {
  const { locale, messages } = useInterfaceLocaleState();
  // Created once: both are provider props, and new functions on each render
  // would re-create every translator below.
  const [{ getMessageFallback, onError }] = useState(() =>
    createIntlPolicy(intlPolicyMode())
  );

  useEffect(() => {
    Sentry.setTag("locale", locale);
  }, [locale]);

  return (
    <IntlProvider
      getMessageFallback={getMessageFallback}
      locale={locale}
      messages={messages}
      onError={onError}
    >
      {children}
    </IntlProvider>
  );
}

/** What "Automatic" resolves to for this host right now. */
export function useAutomaticLocale(): HostLocale {
  return useInterfaceLocaleState().automaticLocale;
}
